import { EllipsisHorizontalIcon } from "@heroicons/react/24/solid";
import { ReactElement, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";

import { ApiParameter } from "../api";
import { HasHttpParameters } from "../types/form-inputs.ts";
import APIParameterAddDialog from "./APIParameterAddDialog.tsx";
import { CardLayout } from "./CardLayout.tsx";
import { Badge } from "./catalyst/Badge.tsx";
import { Button } from "./catalyst/Button.tsx";
import { Dropdown } from "./catalyst/Dropdown.tsx";
import { DropdownButton } from "./catalyst/DropdownButton.tsx";
import { DropdownItem } from "./catalyst/DropdownItem.tsx";
import { DropdownMenu } from "./catalyst/DropdownMenu.tsx";
import { Table } from "./catalyst/Table.tsx";
import { TableBody } from "./catalyst/TableBody.tsx";
import { TableCell } from "./catalyst/TableCell.tsx";
import { TableHead } from "./catalyst/TableHead.tsx";
import { TableHeader } from "./catalyst/TableHeader.tsx";
import { TableRow } from "./catalyst/TableRow.tsx";

export type APIParameterTableProps = {
  currentParameterType: ApiParameter["type"];
};

const APIParameterTable = ({
  currentParameterType,
}: APIParameterTableProps): ReactElement => {
  const { control } = useFormContext<HasHttpParameters>();
  const { append, fields, remove, update } = useFieldArray({
    control,
    name: "httpParameters",
  });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<null | number>(null);
  const [dialogParameter, setDialogParameter] = useState<ApiParameter | null>(
    null,
  );

  const apiParameters = fields as ApiParameter[];

  const openDialog = (index: null | number = null): void => {
    if (index !== null) {
      setEditingIndex(index);
      setDialogParameter(apiParameters[index]);
    } else {
      setEditingIndex(null);
      setDialogParameter(null);
    }
    setIsDialogOpen(true);
  };

  const saveParameter = (data: ApiParameter): void => {
    const fieldArrayData = data;

    if (editingIndex !== null) {
      update(editingIndex, fieldArrayData);
    } else {
      append(fieldArrayData);
    }
    setIsDialogOpen(false);
  };

  const parameterList = fields.map((rawParameter, i) => {
    const parameter = rawParameter;
    if (String(parameter.type) !== currentParameterType) return null;

    return (
      <TableRow key={rawParameter.id}>
        <TableCell className="font-medium">
          <Badge color="zinc">{parameter.name}</Badge>
        </TableCell>
        <TableCell>
          <Badge color="zinc">{parameter.value}</Badge>
        </TableCell>
        <TableCell className="text-zinc-500">{parameter.description}</TableCell>
        <TableCell>
          <Dropdown>
            <DropdownButton aria-label="More options" plain>
              <EllipsisHorizontalIcon />
            </DropdownButton>
            <DropdownMenu>
              <DropdownItem onClick={() => openDialog(i)}>Edit</DropdownItem>
              <DropdownItem onClick={() => remove(i)}>Delete</DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </TableCell>
      </TableRow>
    );
  });

  let tableBody = <>{parameterList}</>;

  const addButton = (
    <Button onClick={() => openDialog()} outline>
      + Add
    </Button>
  );
  if (parameterList.filter((p) => p !== null).length === 0) {
    tableBody = (
      <TableRow>
        <TableCell
          className="border border-gray-300 py-4 text-center text-gray-500"
          colSpan={4}
        >
          <span className="mr-4 text-gray-500">
            No {currentParameterType} parameters
          </span>
          {addButton}
        </TableCell>
      </TableRow>
    );
  }

  return (
    <>
      <CardLayout
        header={
          <>
            <div className="-ml-4 -mt-2 flex flex-wrap items-center justify-between sm:flex-nowrap">
              <div className="ml-4 mt-2">{currentParameterType}</div>
              <div className="ml-4 mt-2 shrink-0">{addButton}</div>
            </div>
          </>
        }
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader className="w-1/4 text-left text-gray-300">
                Name
              </TableHeader>
              <TableHeader className="w-1/4 text-left text-gray-300">
                Value
              </TableHeader>
              <TableHeader className="text-left text-gray-300">
                Description
              </TableHeader>
              <TableHeader className="w-10 text-right" />
            </TableRow>
          </TableHead>
          <TableBody>{tableBody}</TableBody>
        </Table>
      </CardLayout>
      <APIParameterAddDialog
        currentParameterType={currentParameterType}
        editingParameter={dialogParameter}
        isDialogOpen={isDialogOpen}
        saveParameter={saveParameter}
        setIsDialogOpen={setIsDialogOpen}
      />
    </>
  );
};

export default APIParameterTable;
